import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import CssBaseline from '@material-ui/core/CssBaseline';
import List from '@material-ui/core/List';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import SupervisorAccountOutlinedIcon from '@material-ui/icons/SupervisorAccountOutlined';
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import SchoolOutlinedIcon from '@material-ui/icons/SchoolOutlined';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import InsertChartOutlinedIcon from '@material-ui/icons/InsertChartOutlined';
import PieChartOutlinedIcon from '@material-ui/icons/PieChartOutlined';
import TrendingUpOutlinedIcon from '@material-ui/icons/TrendingUpOutlined';
import MemoryOutlinedIcon from '@material-ui/icons/MemoryOutlined';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import PlaylistAddCheckOutlinedIcon from '@material-ui/icons/PlaylistAddCheckOutlined';
import CollectionsBookmarkOutlinedIcon from '@material-ui/icons/CollectionsBookmarkOutlined';
import LocalLibraryOutlinedIcon from '@material-ui/icons/LocalLibraryOutlined';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import FolderOpenOutlinedIcon from '@material-ui/icons/FolderOpenOutlined';
import { useAuthDispatch, useAuthState } from '../../../Context';
import { USER_ROLES } from '../../../utils/constants';
import {
  clearImpersonationLearningHub,
  getImpersonationLearningHubAvailable,
  getLearningHubAccess,
  isLearningHubAvailable,
  LEARNING_HUB_ACCESS_EVENT,
  LEARNING_HUB_AVAILABILITY_EVENT,
  learningHubMenuLabel,
  syncImpersonationLearningHub,
} from '../../../utils/learningHubAvailability';
import { useLocation } from 'react-router-dom';
import { Paths, UserPath } from '../../../Routes/routePaths';
import { useImpersonation } from '../../../hooks/useImpersonation';
import SidebarToggleIcon from '../SidebarToggleIcon';
import MenuItems from './MenuItems';
import { useHistory } from 'react-router';
import variables from '../../../Sys/variable.scss';
import logoImg from '../../../Assets/images/logo.jpg';
import logoDarkImg from '../../../Assets/images/logo-dark.svg';
import logoMarkDarkImg from '../../../Assets/images/logo-mark-dark.svg';
import { useColorMode } from '../../../theme/ColorModeProvider';
import { resolveBrandLogo } from '../Header/brandLogo';

const withLearningHubStatus = (item, access) => {
  if (item.title !== 'Learning Hub') {
    return item;
  }
  const statusLabel = learningHubMenuLabel(access);
  return {
    ...item,
    badge: statusLabel || item.badge,
    tooltip: statusLabel ? `Learning Hub, ${statusLabel}` : item.title,
  };
};
export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_COLLAPSED_WIDTH = 64;
const drawerWidth = SIDEBAR_WIDTH;
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'none',
    position: (props) => (props.constrainHeight ? 'relative' : 'sticky'),
    top: (props) => (props.constrainHeight
      ? undefined
      : 'var(--impersonation-banner-offset, 0px)'),
    height: (props) => (props.constrainHeight
      ? '100%'
      : 'calc(100vh - var(--impersonation-banner-offset, 0px))'),
    minHeight: 0,
    alignSelf: (props) => (props.constrainHeight ? 'stretch' : 'flex-start'),
    '@media (min-width: 1280px)': {
      display: 'flex',
    },
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    height: '100%',
    transition: 'width 0.22s ease',
    [theme.breakpoints.down('sm')]: {
      width: drawerWidth,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    background: variables.sidebarColor,
    border: '0',
    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
    minHeight: 0,
    height: '100%',
    transition: 'width 0.22s ease',
    overflowX: 'hidden',
    [theme.breakpoints.up('sm')]: {
      position: 'relative',
    },
  },
  drawerPaperMobile: {
    width: drawerWidth,
    background: variables.sidebarColor,
    border: '0',
    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
  },
  brand: {
    minHeight: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: (props) => (props.collapsed ? 'center' : 'space-between'),
    gap: (props) => (props.collapsed ? theme.spacing(0.25) : theme.spacing(1)),
    padding: (props) => (props.collapsed
      ? theme.spacing(1, 0.5)
      : theme.spacing(1, 1.25, 1, 2)),
    borderBottom: '1px solid var(--dm-border, rgba(0, 0, 0, 0.08))',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  brandLogo: {
    display: 'block',
    width: 'auto',
    maxWidth: 140,
    maxHeight: 48,
    minWidth: 0,
    flexShrink: 1,
    objectFit: 'contain',
    cursor: 'pointer',
    borderRadius: 6,
  },
  brandLogoOnDark: {
    backgroundColor: '#fff',
    padding: theme.spacing(0.5, 1),
    boxSizing: 'border-box',
  },
  toggleBtn: {
    width: (props) => (props.collapsed ? 32 : 40),
    height: (props) => (props.collapsed ? 32 : 40),
    flexShrink: 0,
    borderRadius: 10,
    border: '1px solid var(--dm-border)',
    color: variables.darkThemeText,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'var(--dm-hover)',
    },
  },
  collapsedBrandToggle: {
    position: 'relative',
    width: 44,
    height: 44,
    padding: 0,
    border: '0',
    '&:hover $collapsedBrandLogo, &:focus-visible $collapsedBrandLogo': {
      opacity: 0,
      transform: 'scale(0.85)',
    },
    '&:hover $collapsedToggleIcon, &:focus-visible $collapsedToggleIcon': {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  collapsedBrandLogo: {
    width: 32,
    height: 32,
    objectFit: 'contain',
    transition: 'opacity 150ms ease, transform 150ms ease',
  },
  collapsedToggleIcon: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transform: 'scale(0.85)',
    transition: 'opacity 150ms ease, transform 150ms ease',
  },
  drawerContainer: {
    overflow: 'auto',
    background: variables.sidebarColor,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  navList: {
    background: variables.sidebarColor,
    padding: (props) => (props.collapsed
      ? theme.spacing(0.5, 0.75, 2)
      : theme.spacing(0.5, 1.25, 2)),
  },
}));

const reportingSubmenus = [
  {
    title: 'Overview',
    icon: <PieChartOutlinedIcon fontSize="small" />,
    active: '/reporting/overview',
  },
  {
    title: 'Learning as a service',
    icon: <SchoolOutlinedIcon fontSize="small" />,
    active: '/reporting/learning-service',
  },
  {
    title: 'Digital Talent Maturity Model',
    icon: <TrendingUpOutlinedIcon fontSize="small" />,
    active: '/reporting/digital-talent-maturity',
  },
  {
    title: 'AI Maturity Model',
    icon: <MemoryOutlinedIcon fontSize="small" />,
    active: '/reporting/digital-ai-maturity',
  },
];

const superAdminMenus = [
  {
    title: 'Dashboard',
    icon: <DashboardOutlinedIcon />,
    active: '/dashboard',
  },
  {
    title: 'Administrators',
    icon: <SupervisorAccountOutlinedIcon />,
    active: '/administrators',
  },
  {
    title: 'Customer Onboarding',
    icon: <PersonAddOutlinedIcon />,
    active: '/flowcustomer',
  },
  {
    title: 'Customers',
    icon: <BusinessOutlinedIcon />,
    active: '/customers',
  },
  {
    title: 'Projects',
    icon: <FolderOpenOutlinedIcon />,
    active: '/superadmin/projects',
  },
  {
    title: 'Users',
    icon: <PeopleOutlineIcon />,
    active: '/users',
  },
  {
    title: 'Courses',
    icon: <SchoolOutlinedIcon />,
    active: '/courses',
  },
  {
    title: 'Assessments',
    icon: <AssignmentOutlinedIcon />,
    active: '/myassessments',
  },
  {
    title: 'Experiments',
    icon: <CategoryOutlinedIcon />,
    active: '/experiments',
  },
  {
    title: 'Deployments',
    icon: <CloudUploadOutlinedIcon />,
    active: '/deployments',
  },
  {
    title: 'Reporting',
    icon: <InsertChartOutlinedIcon />,
    active: '/reporting',
    submenus: reportingSubmenus,
  }
];
const customerAdminMenus = [
  {
    title: 'Dashboard',
    icon: <DashboardOutlinedIcon />,
    active: '/dashboard',
  },
  {
    title: 'Users',
    icon: <PeopleOutlineIcon />,
    active: '/users',
  },
  {
    title: 'Licenses',
    icon: <VpnKeyOutlinedIcon />,
    active: '/licenses',
  },
  {
    title: 'Calendar',
    icon: <EventOutlinedIcon />,
    active: '/calendar',
  },
  {
    title: 'Course Requests',
    icon: <PlaylistAddCheckOutlinedIcon />,
    active: '/course-requests',
  },
  {
    title: 'Learning Hub requests',
    icon: <LocalLibraryOutlinedIcon />,
    active: '/learning-hub-requests',
  },
  {
    title: 'Reporting',
    icon: <InsertChartOutlinedIcon />,
    active: '/reporting',
    submenus: reportingSubmenus,
  }];
const CSMAdminMenus = [
  {
    title: 'Dashboard',
    icon: <DashboardOutlinedIcon />,
    active: '/dashboard',
  },
  {
    title: 'Users',
    icon: <PeopleOutlineIcon />,
    active: '/users',
  },
  {
    title: 'Learning Hub requests',
    icon: <LocalLibraryOutlinedIcon />,
    active: '/learning-hub-requests',
  },
  {
    title: 'Reporting',
    icon: <InsertChartOutlinedIcon />,
    active: '/reporting',
    submenus: reportingSubmenus,
  }];
const userAdminMenus = [
  {
    title: 'Dashboard',
    icon: <DashboardOutlinedIcon />,
    active: Paths.Dashboard,
    alsoActive: [UserPath.UserDashboard],
  },
  {
    title: 'My Courses',
    icon: <CollectionsBookmarkOutlinedIcon />,
    active: UserPath.RegisteredCourses,
    alsoActive: [UserPath.MyLearningHubCourses],
  },
  {
    title: 'Learning Hub',
    icon: <LocalLibraryOutlinedIcon />,
    active: '/learning-hub',
    badge: 'NEW',
  },
  {
    title: 'Course Catalogue',
    icon: <MenuBookOutlinedIcon />,
    active: '/skill-transformation/course-catalogue',
  },
  {
    title: 'Calendar',
    icon: <EventOutlinedIcon />,
    active: '/calendar',
  },
  {
    title: 'Reporting',
    icon: <AssessmentOutlinedIcon />,
    active: '/reporting',
    submenus: reportingSubmenus,
  }
];

function getMenuItemsByRole(role) {
  let getRole = localStorage.getItem('role').trim();
  if (!getRole) {
    localStorage.setItem("role", role)
  }
  if (getRole === USER_ROLES.SUPER_ADMIN) {
    return superAdminMenus;
  } else if (getRole === USER_ROLES.USER) {
    return userAdminMenus;
  } else if (getRole === USER_ROLES.CSM_ADMIN) {
    return CSMAdminMenus
  } else {
    return customerAdminMenus;
  }
}

const Sidebar = ({ mobileOpen, onMobileClose, isMobile, collapsed = false, onToggleCollapse = () => {}, constrainHeight = false }) => {
  const { mode } = useColorMode();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles({ constrainHeight, collapsed });
  const dispatch = useAuthDispatch();
  const { userDetails } = useAuthState(dispatch);
  const impersonation = useImpersonation();
  const storedLogo = resolveBrandLogo(localStorage.getItem('company_logo'), logoImg);
  const useDarkBrand = mode === 'dark' && storedLogo === logoImg;
  const brandLogo = useDarkBrand ? logoDarkImg : storedLogo;
  const isUserRole = (userDetails?.role || USER_ROLES.USER) === USER_ROLES.USER;
  const [showLearningHub, setShowLearningHub] = React.useState(
    () => isUserRole && isLearningHubAvailable()
  );
  const [impersonationHubAvailable, setImpersonationHubAvailable] = React.useState(
    () => getImpersonationLearningHubAvailable() === true
  );
  const [hubAccess, setHubAccess] = React.useState(() => getLearningHubAccess());

  React.useEffect(() => {
    if (!isUserRole && !impersonation.active) {
      setShowLearningHub(false);
      return undefined;
    }

    setShowLearningHub(isLearningHubAvailable());
    setHubAccess(getLearningHubAccess());

    const handleAvailabilityChange = (event) => {
      setShowLearningHub(event.detail.available);
    };
    const handleAccessChange = (event) => {
      setHubAccess(event.detail.access);
    };

    window.addEventListener(LEARNING_HUB_AVAILABILITY_EVENT, handleAvailabilityChange);
    window.addEventListener(LEARNING_HUB_ACCESS_EVENT, handleAccessChange);
    return () => {
      window.removeEventListener(LEARNING_HUB_AVAILABILITY_EVENT, handleAvailabilityChange);
      window.removeEventListener(LEARNING_HUB_ACCESS_EVENT, handleAccessChange);
    };
  }, [isUserRole, impersonation.active]);

  React.useEffect(() => {
    if (!impersonation.active) {
      clearImpersonationLearningHub();
      setImpersonationHubAvailable(false);
      return undefined;
    }

    let cancelled = false;
    setImpersonationHubAvailable(getImpersonationLearningHubAvailable() === true);

    syncImpersonationLearningHub().then((available) => {
      if (!cancelled) {
        setImpersonationHubAvailable(available);
        setHubAccess(getLearningHubAccess());
      }
    });

    return () => {
      cancelled = true;
    };
  }, [impersonation.active]);

  const roleMenus = impersonation.active
    ? userAdminMenus
    : getMenuItemsByRole(userDetails?.role || USER_ROLES.USER);

  const menuItems = roleMenus
    .filter((item) => {
      if (item.title !== 'Learning Hub') {
        return true;
      }
      return impersonation.active ? impersonationHubAvailable : showLearningHub;
    })
    .map((item) => withLearningHubStatus(item, hubAccess));

  const handleItemClick = (path) => {
    if (location.pathname === path) {
      history.replace({
        pathname: path,
        search: location.search,
        hash: location.hash,
        state: {
          ...(location.state && typeof location.state === 'object' ? location.state : {}),
          sidebarNavAt: Date.now(),
        },
      });
    } else {
      history.push({ pathname: path });
    }
    if (isMobile) {
      onMobileClose();
    }
  }

  const renderDrawerContent = (isCollapsed, showDesktopToggle) => (
    <div className={classes.drawerContainer}>
      <div
        className={classes.brand}
        style={!showDesktopToggle ? { justifyContent: 'center', paddingLeft: 16, paddingRight: 16 } : undefined}
      >
        {isCollapsed && showDesktopToggle ? (
          <Tooltip
            title="Open sidebar"
            placement="right"
          >
            <IconButton
              className={`${classes.toggleBtn} ${classes.collapsedBrandToggle}`}
              onClick={(event) => {
                onToggleCollapse();
                event.currentTarget.blur();
              }}
              aria-label="Open sidebar"
              aria-expanded={false}
            >
              <img
                className={classes.collapsedBrandLogo}
                src={mode === 'dark' ? logoMarkDarkImg : `${process.env.PUBLIC_URL}/deviare-minimum-logo.png`}
                loading="eager"
                alt=""
              />
              <span className={classes.collapsedToggleIcon} aria-hidden="true">
                <SidebarToggleIcon />
              </span>
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <img
              className={`${classes.brandLogo}${mode === 'dark' && !useDarkBrand ? ` ${classes.brandLogoOnDark}` : ''}`}
              src={brandLogo}
              alt="Deviare"
              onClick={() => handleItemClick(Paths.Dashboard)}
            />
            {showDesktopToggle ? (
              <Tooltip title="Close sidebar" placement="right">
                <IconButton
                  className={classes.toggleBtn}
                  onClick={(event) => {
                    onToggleCollapse();
                    event.currentTarget.blur();
                  }}
                  aria-label="Close sidebar"
                  aria-expanded
                >
                  <SidebarToggleIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </>
        )}
      </div>
      <List className={classes.navList} disablePadding>
        {menuItems.map((item, index) => (
          <MenuItems
            key={index}
            menuItems={item}
            index={index}
            handleItemClick={handleItemClick}
            location={location}
            collapsed={isCollapsed}
            onRequestExpand={onToggleCollapse}
          />
        ))}
      </List>
    </div>
  );

  const desktopWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <>
      <CssBaseline />
      {/* Desktop drawer */}
      <nav className={classes.root} aria-label="navigation">
        <Drawer
          className={classes.drawer}
          style={{ width: desktopWidth }}
          variant="permanent"
          classes={{
            paper: classes.drawerPaper,
          }}
          PaperProps={{ style: { width: desktopWidth, ...(constrainHeight ? { height: '100%', minHeight: 0 } : {}) } }}
        >
          {renderDrawerContent(collapsed, true)}
        </Drawer>
      </nav>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        classes={{
          paper: classes.drawerPaperMobile,
        }}
      >
        {renderDrawerContent(false, false)}
      </Drawer>
    </>
  );
}

export default Sidebar;
